import _ from 'lodash'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { ExperienceTimePicker } from 'Components/reusable/InputFields/ExperienceTimePicker'
import { Id } from 'types/ids'
import { Context } from '../context'
import { AnyObject, ContextValue, EditableFieldsOfType } from '../types'

/**
 * Password field.
 */
export const DataEditContextExperienceTimeField = <T extends AnyObject>({
  field,
  expandedInputOptions,
  ...rest
}: { field: EditableFieldsOfType<T, number, Id>; expandedInputOptions?: boolean } & Omit<
  Record<string, unknown>,
  'onChange' | 'value' | 'error' | 'name'
>) => {
  const { flatData, errors, disabled, setFields, handleSubmit, localeBase, t } = useContext<ContextValue<T>>(Context)

  const [value, setValue] = useState<string>((flatData[field] as unknown as number).toString())
  const [inputType, setInputType] = useState<string>('months')

  const error = errors[field]

  useEffect(() => {
    if ((flatData[field] as unknown as number) === 0) setValue(flatData[field] as unknown as string)
  }, [field, flatData])

  const setField = useMemo(
    () =>
      _.debounce((newValue: string, inputType: string) => {
        const numberValue = parseFloat(newValue)
        if (inputType === 'months') {
          setFields({ [field]: numberValue })
        } else if (expandedInputOptions) {
          if (inputType === 'days') {
            setFields({ [field]: numberValue / 30.436875 })
          } else if (inputType === 'manMonths') {
            setFields({ [field]: numberValue * 1.369863 })
          } else if (inputType === 'manDays') {
            setFields({ [field]: numberValue / 22 })
          } else {
            setFields({ [field]: numberValue * 12 })
          }
        } else {
          setFields({ [field]: numberValue * 12 })
        }
      }, 300),
    [expandedInputOptions, setFields, field]
  )

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setField(newValue, inputType)
    setValue(newValue)
  }

  return (
    <ExperienceTimePicker
      expandedInputOptions={expandedInputOptions}
      onKeyDown={e => handleSubmit(e)}
      onChange={handleChange}
      inputType={inputType}
      setInputType={type => {
        setInputType(type)
        setField(value, type)
      }}
      type="number"
      value={value ?? null}
      error={error}
      disabled={disabled[field]}
      name={field}
      label={t(`${localeBase}.${field}`)}
      InputProps={{ inputProps: { min: 0 } }}
      {...rest}
    />
  )
}

/**
 * Nullable number field.
 */
// TODO: make this component not accept non-nullable number fields
